import { z } from 'zod';

export const businessHoursEntrySchema = z.object({
  opens: z.string().trim(),
  closes: z.string().trim(),
  closed: z.boolean().optional().default(false),
});

export const serviceSetupSchema = z.object({
  name: z.string().trim().min(1, 'Service name is required'),
  description: z.string().trim().optional().default(''),
  durationMinutes: z.number().int().positive().optional(),
  priceLabel: z.string().trim().optional().default(''),
});

export const staffSetupSchema = z.object({
  name: z.string().trim().min(1, 'Staff member name is required'),
  role: z.string().trim().min(1, 'Staff role is required'),
  email: z.string().trim().email('Please enter a valid email').or(z.literal('')).optional(),
});

export const onboardingSchema = z.object({
  status: z.enum(['not_started', 'in_progress', 'complete']).default('in_progress'),
  businessHours: z.object({
    monday: businessHoursEntrySchema,
    tuesday: businessHoursEntrySchema,
    wednesday: businessHoursEntrySchema,
    thursday: businessHoursEntrySchema,
    friday: businessHoursEntrySchema,
    saturday: businessHoursEntrySchema,
    sunday: businessHoursEntrySchema,
  }),
  timeZone: z.string().trim().min(1, 'Time zone is required'),
  location: z.object({
    addressLine1: z.string().trim().min(1, 'Street address is required'),
    addressLine2: z.string().trim().optional().default(''),
    city: z.string().trim().min(1, 'City is required'),
    region: z.string().trim().min(1, 'State/region is required'),
    postalCode: z.string().trim().min(1, 'Postal code is required'),
    country: z.string().trim().min(1, 'Country is required'),
  }),
  services: z.array(serviceSetupSchema).min(1, 'Add at least one service or class'),
  staff: z.array(staffSetupSchema).min(1, 'Add at least one staff member'),
  bookingSlug: z.string().trim().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers, and hyphens only'),
  logo: z.string().trim().optional().default(''),
  cancellationPolicy: z.string().trim().min(1, 'Cancellation policy is required'),
});

export type OnboardingFormData = z.infer<typeof onboardingSchema>;

export const defaultValues: OnboardingFormData = {
  status: 'in_progress',
  businessHours: {
    monday: { opens: '06:00', closes: '21:00', closed: false },
    tuesday: { opens: '06:00', closes: '21:00', closed: false },
    wednesday: { opens: '06:00', closes: '21:00', closed: false },
    thursday: { opens: '06:00', closes: '21:00', closed: false },
    friday: { opens: '06:00', closes: '20:00', closed: false },
    saturday: { opens: '08:00', closes: '14:00', closed: false },
    sunday: { opens: '08:00', closes: '12:00', closed: true },
  },
  timeZone: 'America/New_York',
  location: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    postalCode: '',
    country: 'US',
  },
  services: [
    {
      name: '',
      description: '',
      durationMinutes: 60,
      priceLabel: '',
    },
  ],
  staff: [
    {
      name: '',
      role: '',
      email: '',
    },
  ],
  bookingSlug: '',
  logo: '',
  cancellationPolicy: '',
};
